import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '@core/api';
import { captureAuthError, isExpectedClientTransportError } from '@core/sentry/utils';
import type { OAuthPrefill } from '../types';
import { routeOAuthResponse, type OAuthBackendResponse } from '../utils/routeOAuthResponse';

/**
 * Google Identity Services types — OAuth 2.0 authorization code flow.
 * Uses google.accounts.oauth2 (NOT google.accounts.id which returns ID tokens).
 * This matches the Satellizer authorization code flow the backend expects.
 */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          prompt: () => void;
        };
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode: 'popup' | 'redirect';
            callback: (response: { code?: string; error?: string }) => void;
          }) => { requestCode: () => void };
        };
      };
    };
  }
}

interface UseGoogleSignInOptions {
  /** Called when Google returned a JWT — the user is already registered. */
  onSuccess: (token: string, email: string) => Promise<void> | void;
  /**
   * Called when Google authenticated but the user is not yet in Meitre.
   * The backend response shape is `{ uid, email, fullname }` with no token.
   * The host page should navigate to the signup form prefilled with this
   * data instead of asking the user to pick another method. Matches the
   * legacy behavior in `meitre-frontend/src/app/home/controllers/modal/user/signup.js`.
   *
   * Optional so the hook stays usable by callers that only care about the
   * "already registered" branch. When omitted, a new-user response falls
   * back to the same error path as a genuinely broken response, so the
   * user still gets a readable message instead of a silent hang.
   */
  onNeedsSignup?: (prefill: OAuthPrefill) => void;
}

export function useGoogleSignIn({ onSuccess, onNeedsSignup }: UseGoogleSignInOptions) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  // Hold the latest callback references in refs so `signIn` stays referentially
  // stable across re-renders. Parents that pass inline functions would
  // otherwise recreate `signIn` on every render, invalidating `useEffect`
  // dependencies that consume it (for example the `providerHint` auto-trigger
  // in AuthLoginPage).
  const onSuccessRef = useRef(onSuccess);
  const onNeedsSignupRef = useRef(onNeedsSignup);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onNeedsSignupRef.current = onNeedsSignup;
  }, [onSuccess, onNeedsSignup]);

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Load Google Identity Services SDK
  useEffect(() => {
    if (!clientId || initRef.current) return;
    initRef.current = true;

    if (window.google?.accounts?.oauth2) {
      setIsReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsReady(true);
    script.onerror = () => {
      console.warn('[Auth] Failed to load Google Identity Services');
    };
    document.head.appendChild(script);
  }, [clientId]);

  const signIn = useCallback(async () => {
    if (!window.google?.accounts?.oauth2 || !clientId) {
      setError('Google Sign-In is not available');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // Use authorization code flow (matches Satellizer backend expectations)
      const code = await new Promise<string>((resolve, reject) => {
        // Safety timeout: Google's SDK does not invoke the callback when the
        // user closes the popup manually, so without an upper bound the
        // Promise could stay pending indefinitely and freeze the UI in a
        // loading state. Keep the id so the callback can cancel the timer on
        // early settle — otherwise the pending timeout pins the whole
        // closure (codeClient, callback) in memory for 2 minutes per attempt.
        // The `settled` flag guards against a rare race where the SDK
        // callback fires the same tick the timer dispatches.
        let settled = false;
        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          fn();
        };
        const codeClient = window.google!.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile',
          ux_mode: 'popup',
          callback: (response) => {
            if (response.error) {
              settle(() => reject(new Error(response.error!)));
            } else if (response.code) {
              settle(() => resolve(response.code!));
            } else {
              settle(() => reject(new Error('No authorization code received')));
            }
          },
        });
        codeClient.requestCode();
        const timeoutId = setTimeout(
          () => settle(() => reject(new Error('timeout'))),
          120_000,
        );
      });

      // Send authorization code to backend. The response has two shapes
      // distinguished by whether the user is already registered in Meitre:
      //   - Existing user → `{ token, email }` (logged in directly)
      //   - New user      → `{ uid, email, fullname }` (needs signup prefill)
      const result = await api.post<OAuthBackendResponse>(
        '/auth/google',
        { code, clientId, redirectUri: 'postmessage' },
        { skipAuth: true },
      );

      const action = routeOAuthResponse('google', result);
      switch (action.kind) {
        case 'success':
          await onSuccessRef.current(action.token, action.email);
          return;
        case 'needs-signup':
          if (!onNeedsSignupRef.current) break; // fall through to error path
          onNeedsSignupRef.current(action.prefill);
          return;
        case 'facebook-missing-email':
          // Never fires for Google per `routeOAuthResponse`. Kept here so
          // the switch is exhaustive at the type level.
          break;
        case 'error':
          break; // handled below
      }

      // Fall-through for error cases (including `needs-signup` without a
      // callback). Keep the distinct Sentry signal from PR #103 so we can
      // tell "new user needing signup" from "genuinely broken response".
      captureAuthError('auth.oauth_response_missing_token', {
        provider: 'google',
        hasEmail: action.kind === 'error' ? action.hasEmail : false,
        hasUserId: action.kind === 'error' ? action.hasUserId : false,
        hasUid: action.kind === 'error' ? action.hasUid : false,
        responseKeys: action.kind === 'error' ? action.responseKeys : [],
      });
      setError('Google sign-in did not complete. Please try another method.');
    } catch (err) {
      if (err instanceof Error && (err.message === 'timeout' || err.message === 'access_denied')) {
        // User closed popup or denied — not an error
        setIsLoading(false);
        return;
      }
      if (!isExpectedClientTransportError(err)) {
        Sentry.captureException(err, { tags: { auth: 'google' } });
      }
      setError('Google sign-in failed. Please try another method.');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  return { signIn, isReady: isReady && !!clientId, isLoading, error };
}
